import { IPrepareHitsUrl } from '@omni/kit/services/SearchService';
import {
  DEFAULT_PAGE_SIZE,
  DOMAIN_APPS,
  DOMAIN_GROUPS,
  DOMAIN_LIBRARY,
} from '@omni/kit/services/SearchService/Constants';

import { Domain } from '../types';
import { extractValuesFromArray, getTimeRange } from './filterOptions';

export interface SearchConfigParams {
  domain: Domain;
  page?: number;
  query?: string;
  appKey?: string;
  rootAppKey?: string;
  appFilterValue?: string;
  locationQuery?: string;
  selectedGroupType?: Array<{ label: string; value: string }>;
  selectedGroupLocations?: Array<{
    label: string;
    value: any; // This will contain the location object with city/state
  }>;
  selectedFrequency?: Array<{ label: string; value: string }>;
  selectedDay?: Array<{ label: string; value: string }>;
  selectedTimeOfDay?: Array<{ label: string; value: string }>;
  groupTypeFilterValue?: string;
  appTimezone?: string;
}

/**
 * Builds the search configuration object that gets passed to SearchService.search
 *
 * IMPORTANT: This function should NOT pre-encode parameters with encodeURIComponent.
 * The prepareHitsUrl function will handle URL encoding automatically.
 * Pre-encoding here would cause double-encoding in the final URL.
 */
export function buildSearchConfig(params: SearchConfigParams): IPrepareHitsUrl {
  const {
    domain,
    page = 1,
    query = '',
    appKey,
    rootAppKey,
    appFilterValue,
    locationQuery,
    selectedGroupType = [],
    selectedGroupLocations = [],
    selectedFrequency = [],
    selectedDay = [],
    selectedTimeOfDay = [],
    groupTypeFilterValue,
    appTimezone,
  } = params;

  // IMPORTANT: filterQuery and query must be raw JSON string (not URL encoded)
  // The BaseService.buildUrl() will handle encoding automatically.
  const config: IPrepareHitsUrl = {
    domain: domain,
    page,
    size: DEFAULT_PAGE_SIZE,
    ...(domain === DOMAIN_APPS ? { containerAppKey: rootAppKey } : {}),
    ...(domain === DOMAIN_LIBRARY
      ? {
          appFilterValue,
          type: ['media-item', 'tag:speaker', 'tag:topic'],
        }
      : {}),
    ...(locationQuery
      ? {
          query: query ? `${query}*` : undefined,
          filterQuery: `{"loc":["${locationQuery}"],"dist":30}`,
        }
      : {
          query: `${query}*`,
        }),
    ...(domain === DOMAIN_GROUPS
      ? {
          filterQuery: `{"app_key":"${appKey}"${
            selectedGroupType.length !== 0
              ? `,"group_type": ${JSON.stringify(
                  extractValuesFromArray(selectedGroupType)
                )}`
              : `,"group_type": "${
                  groupTypeFilterValue ? groupTypeFilterValue : ''
                }"`
          }${
            selectedGroupLocations.length !== 0
              ? `,"location":${JSON.stringify(
                  selectedGroupLocations.map((item: any) => ({
                    city: item.value.city,
                    state: item.value.state,
                  }))
                )}`
              : ''
          }${
            selectedFrequency.length !== 0
              ? `,"frequency":${JSON.stringify(
                  extractValuesFromArray(selectedFrequency)
                )}`
              : ''
          }${
            selectedDay.length !== 0
              ? `,"day_of_week":${JSON.stringify(
                  extractValuesFromArray(selectedDay)
                )}`
              : ''
          }${
            selectedTimeOfDay.length !== 0
              ? `,"time_of_day":${JSON.stringify(
                  getTimeRange(selectedTimeOfDay)
                )},"timezone":"${appTimezone}"`
              : ''
          }}`,
          query: query ? `${query}*` : 'matchall',
        }
      : {}),
  };

  return config;
}
